
import { useState, useRef, useEffect } from 'react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useSilenceDetection } from '@/hooks/useSilenceDetection';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';

interface UseVoiceRecordingProps {
  silenceDetectionEnabled: boolean;
  onRecordingComplete: () => void;
  debug?: boolean;
}

export const useVoiceRecording = ({ 
  silenceDetectionEnabled, 
  onRecordingComplete,
  debug = false
}: UseVoiceRecordingProps) => {
  const [isListening, setIsListening] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const canStopRef = useRef(false);
  const isStoppingRef = useRef(false);
  const hasCompletedRef = useRef(false); // Add this to prevent duplicate completion calls

  const {
    startRecording,
    stopRecording,
    blob: audioBlob,
    reset: resetRecording,
    recordingTime,
  } = useAudioRecorder();

  const handleStopListening = async () => {
    if (!isListening || isStoppingRef.current || !canStopRef.current || hasCompletedRef.current) {
      if (debug) console.log("🚫 Stop listening blocked - not ready or already completed");
      return;
    }
    
    isStoppingRef.current = true;
    hasCompletedRef.current = true; // Mark as completed immediately
    canStopRef.current = false;
    
    if (debug) console.log("🛑 Stopping listening and cleaning up...");
    setIsListening(false);
    
    stopRecording();
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        if (debug) console.log(`🔇 Stopped microphone track: ${track.kind}`);
      });
      streamRef.current = null;
    }
    
    cleanupSpeechRecognition();
    cleanupAudioContext();
    
    // Reset flags after cleanup
    setTimeout(() => {
      isStoppingRef.current = false;
      onRecordingComplete();
    }, 100);
  };

  const { setupSilenceDetection, cleanupAudioContext } = useSilenceDetection({
    onSilenceDetected: () => {
      if (silenceDetectionEnabled && isListening && canStopRef.current && !isStoppingRef.current && !hasCompletedRef.current) {
        if (debug) console.log("🔇 Silence detected, stopping recording");
        handleStopListening();
      }
    },
    silenceThreshold: 12,
    silenceDelay: 3000,
    requiredSilentFrames: 10,
    debug: false
  });

  const { startSpeechRecognition, cleanupSpeechRecognition } = useSpeechRecognition({
    onSpeechEnd: () => {
      if (debug) console.log("🗣️ Speech recognition detected end (not triggering stop - silence detection handles this)");
      // Don't trigger stop here - let silence detection handle it to avoid duplicates
    },
    debug: false
  });

  // Max recording duration safety (30 seconds)
  useEffect(() => {
    if (recordingTime >= 30 && isListening && canStopRef.current && !isStoppingRef.current && !hasCompletedRef.current) {
      if (debug) console.log("⏱️ Max recording time reached (30s), stopping recording");
      handleStopListening();
    }
  }, [recordingTime, isListening, debug]);

  const startListening = async () => {
    if (debug) console.log("🎤 Starting voice recording...");
    
    // Reset all flags
    canStopRef.current = false;
    isStoppingRef.current = false;
    hasCompletedRef.current = false; // Reset completion flag
    
    // Clean up any previous session
    cleanupAudioContext();
    cleanupSpeechRecognition();
    
    setIsListening(true);
    resetRecording();
    
    try {
      // Start recording and get the stream
      const stream = await startRecording();
      streamRef.current = stream;
      
      // Setup silence detection if enabled
      if (silenceDetectionEnabled && stream) {
        if (debug) console.log("🔧 Setting up silence detection...");
        setupSilenceDetection(stream);
      }
      
      // Start speech recognition
      startSpeechRecognition();
      
      // Enable stopping after a delay to prevent race conditions
      setTimeout(() => {
        canStopRef.current = true;
        if (debug) console.log("✅ Voice recording ready for stopping");
      }, 1500); // 1.5 second delay
      
    } catch (error) {
      console.error("Error starting voice recording:", error);
      setIsListening(false);
      canStopRef.current = false;
      isStoppingRef.current = false;
      hasCompletedRef.current = false;
    }
  };

  return {
    isListening,
    audioBlob,
    recordingTime,
    startListening,
    stopListening: handleStopListening,
    resetRecording
  };
};
